import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';

import Icon from '../../components/AppIcon';
import BasicDetailsStep from './components/BasicDetailsStep';
import ContextStep from './components/ContextStep';
import SummaryStep from './components/SummaryStep';
import ProgressIndicator from './components/ProgressIndicator';

const OccasionCreation = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Basic Details
    occasionType: '',
    customOccasionType: '',
    date: '',
    time: '',
    recipient: '',
    recipientId: null,
    
    // Context
    relationship: '',
    significance: '',
    budgetRange: '',
    notes: '',
    reminderSettings: {
      enabled: true,
      days: [7, 3, 1],
      times: ['09:00']
    },
    
    // Advanced options
    isRecurring: false,
    recurringPattern: 'yearly',
    syncWithCalendar: true,
    tags: []
  });
  
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraft, setIsDraft] = useState(false);

  const steps = [
    { number: 1, title: 'Basic Details', description: 'Occasion & recipient' },
    { number: 2, title: 'Context', description: 'Relationship & preferences' },
    { number: 3, title: 'Summary', description: 'Review & save' }
  ];

  // Load draft if exists
  useEffect(() => {
    const savedDraft = localStorage.getItem('occasion-creation-draft');
    if (savedDraft) {
      try {
        const draftData = JSON.parse(savedDraft);
        setFormData(draftData);
        setIsDraft(true);
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, []);

  // Auto-save draft
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.occasionType || formData.recipient) {
        localStorage.setItem('occasion-creation-draft', JSON.stringify(formData));
      }
    }, 2000);

    return () => clearTimeout(timeoutId);
  }, [formData]);

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear validation error when field is updated
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateStep = (step) => {
    const errors = {};
    
    if (step === 1) {
      if (!formData.occasionType && !formData.customOccasionType) {
        errors.occasionType = 'Please select or enter an occasion type';
      }
      if (!formData.date) {
        errors.date = 'Please select a date';
      }
      if (!formData.recipient) {
        errors.recipient = 'Please select or enter a recipient';
      }
    }
    
    if (step === 2) {
      if (!formData.relationship) {
        errors.relationship = 'Please specify your relationship';
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;
    
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Clear draft on successful submission
      localStorage.removeItem('occasion-creation-draft');
      
      // Navigate to occasions page with success message
      navigate('/occasions', { 
        state: { 
          message: 'Occasion created successfully!',
          occasionData: formData 
        } 
      });
      
    } catch (error) {
      console.error('Error creating occasion:', error);
      // Handle error (show toast, etc.)
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDraft = () => {
    localStorage.setItem('occasion-creation-draft', JSON.stringify(formData));
    setIsDraft(true);
    // Show success message
    console.log('Draft saved successfully');
  };

  const handleClearDraft = () => {
    localStorage.removeItem('occasion-creation-draft');
    setFormData({
      occasionType: '',
      customOccasionType: '',
      date: '',
      time: '',
      recipient: '',
      recipientId: null,
      relationship: '',
      significance: '',
      budgetRange: '',
      notes: '',
      reminderSettings: {
        enabled: true,
        days: [7, 3, 1],
        times: ['09:00']
      },
      isRecurring: false,
      recurringPattern: 'yearly',
      syncWithCalendar: true,
      tags: []
    });
    setIsDraft(false);
    setCurrentStep(1);
    setValidationErrors({});
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicDetailsStep
            formData={formData}
            updateFormData={updateFormData}
            validationErrors={validationErrors}
          />
        );
      case 2:
        return (
          <ContextStep
            formData={formData}
            updateFormData={updateFormData}
            validationErrors={validationErrors}
          />
        );
      case 3:
        return (
          <SummaryStep
            formData={formData}
            updateFormData={updateFormData}
            onEdit={(step) => setCurrentStep(step)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface via-white to-primary-50">
      <Header />
      
      <main className="pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center shadow-warm">
                <Icon name="Plus" size={32} color="white" />
              </div>
              <div className="text-left">
                <h1 className="font-heading font-bold text-3xl lg:text-4xl text-accent-800">
                  Create New Occasion
                </h1>
                <p className="text-secondary-600 font-accent text-lg">
                  Set up your special moment
                </p>
              </div>
            </div>
            
            {isDraft && (
              <div className="bg-warning-50 border border-warning-200 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Icon name="FileText" size={20} className="text-warning-600" />
                    <span className="text-warning-800 font-medium">Draft available</span>
                  </div>
                  <button
                    onClick={handleClearDraft}
                    className="text-warning-600 hover:text-warning-800 text-sm underline"
                  >
                    Clear draft
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Progress Indicator */}
          <div className="mb-8">
            <ProgressIndicator
              steps={steps}
              currentStep={currentStep}
              completedSteps={currentStep - 1}
            />
          </div>

          {/* Form Container */}
          <div className="bg-white rounded-2xl shadow-lg border border-accent-200 overflow-hidden">
            {/* Step Content */}
            <div className="p-6 lg:p-8">
              {renderStepContent()}
            </div>

            {/* Navigation */}
            <div className="bg-surface px-6 py-4 lg:px-8 border-t border-accent-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {currentStep > 1 && (
                    <Button
                      variant="ghost"
                      iconName="ChevronLeft"
                      iconPosition="left"
                      onClick={handlePrevious}
                      className="text-accent-600"
                    >
                      Previous
                    </Button>
                  )}
                  
                  <Button
                    variant="outline"
                    iconName="Save"
                    iconPosition="left"
                    onClick={handleSaveDraft}
                    className="text-accent-600 border-accent-300"
                  >
                    Save Draft
                  </Button>
                </div>

                <div className="flex items-center space-x-4">
                  <Button
                    variant="ghost"
                    onClick={() => navigate('/occasions')}
                    className="text-accent-600"
                  >
                    Cancel
                  </Button>
                  
                  {currentStep < steps.length ? (
                    <Button
                      variant="primary"
                      iconName="ChevronRight"
                      iconPosition="right"
                      onClick={handleNext}
                      className="shadow-warm hover:shadow-primary"
                    >
                      Next Step
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      iconName="Check"
                      iconPosition="left"
                      onClick={handleSubmit}
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      className="shadow-warm hover:shadow-primary"
                    >
                      {isSubmitting ? 'Creating...' : 'Create Occasion'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Tips */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-4 border border-accent-200">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Lightbulb" size={16} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-accent-800 mb-1">Smart Suggestions</h4>
                  <p className="text-sm text-text-secondary">
                    We'll suggest gifts based on your occasion details
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-accent-200">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-secondary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Bell" size={16} className="text-secondary" />
                </div>
                <div>
                  <h4 className="font-medium text-accent-800 mb-1">Smart Reminders</h4>
                  <p className="text-sm text-text-secondary">
                    Get timely reminders so you never miss an occasion
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 border border-accent-200">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Calendar" size={16} className="text-accent-600" />
                </div>
                <div>
                  <h4 className="font-medium text-accent-800 mb-1">Calendar Sync</h4>
                  <p className="text-sm text-text-secondary">
                    Automatically sync with your calendar apps
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OccasionCreation;